import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { UserIcon, WarningIcon, CheckCircleIcon, NotePencilIcon, ArrowsClockwiseIcon } from '@/components/icons';
import Spinner from '@/components/ui/spinner';
import { useI18n } from '@/i18n/I18nProvider';
import { useGetStudentIdhInfo } from '@/lib/hooks/useGetStudentIdhInfo';
import { useStudentSync } from '@/lib/hooks/useStudentSync';
import { useToastNotifications } from '@/lib/hooks/use-toast-notifications';
import { formatEmiratesId } from '@/lib/formatters';
import type { PartnershipCharterResponse } from '@/lib/hooks/useGetCharter';

const PLACEHOLDER = '-';

function InfoField({
  label,
  value,
  span = 1,
  mono = false,
  isRequired = false,
  isMissing = false,
}: {
  label: string;
  value?: React.ReactNode;
  span?: 1 | 2;
  mono?: boolean;
  isRequired?: boolean;
  isMissing?: boolean;
}) {
  return (
    <div className={`space-y-2 ${span === 2 ? 'md:col-span-2' : ''}`}>
      <div className="flex items-center gap-2">
        <div className="block text-sm font-medium text-foreground mb-1">
          {label}
          {isRequired && <span className="text-destructive ml-1">*</span>}
        </div>
        {isMissing && (
          <span className="text-xs text-destructive bg-destructive/10 px-2 py-0.5 rounded">مطلوب / Required</span>
        )}
      </div>
      <div
        className={`bg-muted rounded px-3 py-2 text-sm ${mono ? 'font-mono' : ''} ${isMissing ? 'border-2 border-destructive' : ''}`}
      >
        {value ?? PLACEHOLDER}
      </div>
    </div>
  );
}

interface ParentInfoCardProps {
  studentId: string;
  charterRecord?: PartnershipCharterResponse | null;
  onCanProceedChange?: (canProceed: boolean) => void;
  onDataSynced?: () => Promise<void> | void;
}

export function ParentInfoCard({
  studentId,
  charterRecord,
  onCanProceedChange,
  onDataSynced,
}: ParentInfoCardProps) {
  const { t, locale } = useI18n();
  const toast = useToastNotifications();
  const { syncStudents } = useStudentSync();
  const [isSyncing, setIsSyncing] = React.useState(false);

  const {
    idhData,
    statusId: idhStatusId,
    isLoading: isIdhLoading,
    mutate: mutateIdh,
  } = useGetStudentIdhInfo(studentId, {
    swrOptions: { dedupingInterval: 0 },
  });

  const getStatusText = React.useCallback((statusId: number | null, currentLocale: string): string => {
    if (statusId === null) {
      return currentLocale === 'ar' ? 'لم يتم تقديم طلب لتحديث البيانات' : 'No application submitted';
    }
    const statusMap: Record<number, { ar: string; en: string }> = {
      1: { ar: 'قيد الإجراء', en: 'Pending' },
      2: { ar: 'قيد المراجعة', en: 'Under Review' },
      3: { ar: 'قيد التحديث', en: 'Updating' },
      4: { ar: 'مكتمل', en: 'Complete' },
      5: { ar: 'مرفوض', en: 'Rejected' },
    };
    const localeKey = currentLocale === 'ar' ? 'ar' : 'en';
    return (
      statusMap[statusId]?.[localeKey] ||
      (currentLocale === 'ar' ? 'لم يتم تقديم طلب لتحديث البيانات' : 'No application submitted')
    );
  }, []);

  const idhStatusText = React.useMemo(
    () => getStatusText(idhStatusId, locale === 'ar' ? 'ar' : 'en'),
    [idhStatusId, locale, getStatusText]
  );

  const canProceedWithIDH = React.useMemo(() => {
    if (isIdhLoading) return true;
    if (!idhData) return false;
    if (idhStatusId === null) return false;
    return idhStatusId === 4;
  }, [idhData, idhStatusId, isIdhLoading]);

  React.useEffect(() => {
    onCanProceedChange?.(canProceedWithIDH);
  }, [canProceedWithIDH, onCanProceedChange]);

  const handleSyncUpdate = React.useCallback(async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      await syncStudents();
      await onDataSynced?.();
      toast.success(
        locale === 'ar' ? 'تم بنجاح' : 'Success',
        locale === 'ar'
          ? 'تم تحديث البيانات بنجاح. يمكنك الآن المتابعة.'
          : 'Data synced successfully. You can now proceed.'
      );
    } catch (error: any) {
      toast.error(
        locale === 'ar' ? 'خطأ' : 'Error',
        error?.message || (locale === 'ar' ? 'فشل تحديث البيانات.' : 'Failed to sync data.')
      );
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, syncStudents, onDataSynced, toast, locale]);

  const parentFullName = charterRecord?.parentName || PLACEHOLDER;
  const parentEid = charterRecord?.parentEmiratesId || PLACEHOLDER;
  const primaryPhone = charterRecord?.studentPhone || PLACEHOLDER;
  const studentEmail = charterRecord?.studentEmail || PLACEHOLDER;
  const studentFullName = charterRecord?.studentName || PLACEHOLDER;
  const studentNationalId = charterRecord?.studentEmiratesId || PLACEHOLDER;
  const studentAddress = charterRecord?.studentAddress || PLACEHOLDER;
  const hasValidStudentAddress = charterRecord?.hasValidStudentAddress ?? true;

  return (
    <div className="space-y-6">
      {isIdhLoading && (
        <Card className="border border-blue-200 shadow-sm bg-blue-50/50 dark:bg-blue-900/20">
          <CardContent className="pt-4">
            <div className="mx-auto">
              <Spinner />
            </div>
          </CardContent>
        </Card>
      )}

      {idhData && idhStatusId === 4 && (
        <Card className="border-2 border-green-500 shadow-sm bg-green-50/50 dark:bg-green-900/20">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <CheckCircleIcon />
              <div className="flex-1">
                <p className="font-semibold text-green-800 dark:text-green-300 mb-1">
                  {locale === 'ar' ? 'تأكيد تحديث ملفك الشخصي' : 'Confirm Your Profile Update'}
                </p>
                <p className="text-sm text-green-700 dark:text-green-400 mb-3">
                  {locale === 'ar'
                    ? 'تم تحديث بيانات الطالب بنجاح. يرجى الضغط على زر "مزامنة البيانات" لتأكيد التحديث ثم المتابعة لتوقيع الميثاق.'
                    : 'Student data has been successfully updated. Please click "Sync Data" button to confirm the update, then proceed to sign the charter.'}
                </p>
                <div className="bg-white dark:bg-green-950 rounded p-3 text-xs mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-green-900 dark:text-green-200">
                      {locale === 'ar' ? 'الحالة:' : 'Status:'}
                    </span>
                    <span className="px-2 py-0.5 bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100 rounded font-medium">
                      {idhStatusText}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSyncUpdate}
                  disabled={isSyncing}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all shadow-sm hover:shadow-lg text-sm w-full justify-center ${
                    isSyncing
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isSyncing ? (
                    <>
                      <ArrowsClockwiseIcon className="animate-spin" />
                      {locale === 'ar' ? 'جاري المزامنة...' : 'Syncing...'}
                    </>
                  ) : (
                    <>
                      <ArrowsClockwiseIcon />
                      {locale === 'ar' ? 'مزامنة البيانات' : 'Sync Data'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {idhStatusId !== 4 && (
        <Card className="border-2 border-orange-500 shadow-sm bg-orange-50/70 dark:bg-orange-950/20">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <WarningIcon />
              <div className="flex-1">
                <p className="font-semibold text-orange-800 dark:text-orange-300 mb-1">
                  {locale === 'ar' ? 'يجب تحديث بيانات الطالب' : 'Student Data Update Required'}
                </p>
                <p className="text-sm text-orange-700 dark:text-orange-400 mb-3">
                  {locale === 'ar'
                    ? 'لا يمكن توقيع الميثاق حاليًا. يجب تحديث بيانات الطالب أولاً قبل المتابعة.'
                    : 'Charter signing is currently unavailable. Student data must be updated first before proceeding.'}
                </p>
                <div className="bg-white dark:bg-orange-950 rounded p-3 text-xs">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-medium text-orange-900 dark:text-orange-200">
                      {locale === 'ar' ? 'الحالة الحالية:' : 'Current Status:'}
                    </span>
                    <span className="px-2 py-0.5 bg-orange-200 dark:bg-orange-800 text-orange-900 dark:text-orange-100 rounded font-medium">
                      {idhStatusText}
                    </span>
                  </div>
                  <Link
                    to={studentId ? `/students/${encodeURIComponent(studentId)}` : '/dashboard'}
                    className="aegov-btn btm-sm bg-orange-600 hover:bg-orange-700 w-full"
                  >
                    <NotePencilIcon />
                    {locale === 'ar' ? 'تحديث بيانات الطالب' : 'Update Student Profile'}
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border shadow-sm">
        <CardHeader className="rounded-t-xl bg-primary text-primary-foreground">
          <CardTitle className="text-lg flex items-center">
            <UserIcon className="me-2" />
            {t.parentConduct.parentSection.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <section className="space-y-3">
              <h4 className="text-sm font-semibold text-primary-foreground/80">
                {t.parentConduct.parentSection.parentData}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InfoField label={t.parentConduct.parentSection.parentName} value={parentFullName} />
                <InfoField label={t.parentConduct.parentSection.parentNationalId} value={formatEmiratesId(parentEid)} mono />
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="block text-sm font-medium text-foreground mb-1">
                      {t.parentConduct.parentSection.contactNumber}
                      <span className="text-destructive ml-1">*</span>
                    </div>
                  </div>
                  <div className="bg-muted rounded px-3 py-2 text-sm">{primaryPhone || PLACEHOLDER}</div>
                </div>
                <InfoField
                  label={t.parentConduct.parentSection.parentEmail}
                  value={studentEmail || PLACEHOLDER}
                />
              </div>
            </section>

            <Separator />

            <section className="space-y-3">
              <h4 className="text-sm font-semibold text-primary-foreground/80">
                {t.parentConduct.parentSection.linkedStudentData}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InfoField label={t.parentConduct.parentSection.studentFullName} value={studentFullName} />
                <InfoField label={t.parentConduct.schoolSection.nationalId} value={formatEmiratesId(studentNationalId)} mono />
                <InfoField
                  label={t.parentConduct.parentSection.parentAddress}
                  value={studentAddress}
                  span={2}
                  isRequired={true}
                  isMissing={!hasValidStudentAddress}
                />
              </div>
            </section>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
